import { useEffect, useState } from "react";
import { searchUsers } from "../../api/client";
import type { UserSearchResult } from "../../api/types";
import { useDebounce } from "../../lib/useDebounce";

export type SearchStatus = "idle" | "loading" | "success" | "error";

export interface UserSearchState {
  status: SearchStatus;
  results: UserSearchResult[];
  error: string | null;
}

const MIN_QUERY_LENGTH = 2;

/** Debounced email/name search against the provisioning API. */
export function useUserSearch(query: string, token: string): UserSearchState {
  const debounced = useDebounce(query.trim(), 300);
  const [state, setState] = useState<UserSearchState>({ status: "idle", results: [], error: null });

  useEffect(() => {
    if (debounced.length < MIN_QUERY_LENGTH) {
      setState({ status: "idle", results: [], error: null });
      return;
    }

    const controller = new AbortController();
    setState((prev) => ({ ...prev, status: "loading", error: null }));

    searchUsers(debounced, token, controller.signal)
      .then((results) => setState({ status: "success", results, error: null }))
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setState({
          status: "error",
          results: [],
          error: err instanceof Error ? err.message : "Search failed.",
        });
      });

    return () => controller.abort();
  }, [debounced, token]);

  return state;
}
